/**
 * The Clinics & Appointments manager's tools beside the day board.
 *
 *   ClinicsManager    the hospital's doctors and their clinics: schedules,
 *                     capacity, prices, a new clinic, a day off
 *   CheckupRequests   the checkup requests the desk and doctors route here
 *   DaySchedulePrint  the day's schedule, on paper, one table per clinic
 *
 * Every write is scoped server-side to this hospital's doctors
 * (routes/operations.js doctorOfHospital) — the legacy /api/clinics routes
 * check nothing but the token, and none of this calls them.
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Input, Modal, Spinner, DateInput, TimeInput } from '../../components/common';
import { clinicsAdminApi, requestsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../domain/rbac';
import { RequestCard } from '../hospitalShared/RequestCard';
import { ResultForm } from '../hospitalShared/ResultForm';
import { NoteDialog } from '../hospitalShared/NoteDialog';
import { formatMoney, printArea } from '../hospitalShared/labels';
import { timeRange } from '../../domain/bidi';

const DAYS = [
  ['saturday', 'السبت'],
  ['sunday', 'الأحد'],
  ['monday', 'الإثنين'],
  ['tuesday', 'الثلاثاء'],
  ['wednesday', 'الأربعاء'],
  ['thursday', 'الخميس'],
  ['friday', 'الجمعة'],
];

const blankClinic = () => ({
  name: '',
  startTime: '09:00',
  endTime: '17:00',
  duration: 20,
  maxBookingsPerSlot: 1,
  examination_price: '',
  followup_price: '',
  workingDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
});

function ClinicForm({ value, onChange }) {
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const days = Array.isArray(value.workingDays) ? value.workingDays.map((d) => String(d).toLowerCase()) : [];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="اسم العيادة" required>
          <Input value={value.name || ''} onChange={set('name')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="من">
            <TimeInput value={value.startTime || ''} onChange={set('startTime')} />
          </Field>
          <Field label="إلى">
            <TimeInput value={value.endTime || ''} onChange={set('endTime')} />
          </Field>
        </div>
        <Field label="مدة الكشف (دقيقة)">
          <Input type="number" min="5" value={value.duration ?? ''} onChange={set('duration')} />
        </Field>
        <Field label="السعة في الموعد الواحد">
          <Input type="number" min="1" value={value.maxBookingsPerSlot ?? 1} onChange={set('maxBookingsPerSlot')} />
        </Field>
        <Field label="سعر الكشف (ج.م)">
          <Input type="number" min="0" value={value.examination_price ?? ''} onChange={set('examination_price')} />
        </Field>
        <Field label="سعر الاستشارة (ج.م)">
          <Input type="number" min="0" value={value.followup_price ?? ''} onChange={set('followup_price')} />
        </Field>
      </div>
      <div>
        <p className="field-label">أيام العمل</p>
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map(([key, label]) => {
            const on = days.includes(key);
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ ...value, workingDays: on ? days.filter((d) => d !== key) : [...days, key] })}
                className={`min-h-[40px] rounded-xl border px-3 text-xs font-bold ${
                  on ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-100' : 'border-white/10 text-slate-400'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ClinicsManager({ onChanged }) {
  const { can } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [doctors, setDoctors] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [doctorEdit, setDoctorEdit] = useState(null);
  const [closing, setClosing] = useState(null);
  const [busy, setBusy] = useState(false);

  const mayManage = can(PERMISSIONS.CLINIC_MANAGE);
  const mayClose = can(PERMISSIONS.SCHEDULE_CONFIGURE);

  const load = useCallback(async () => {
    try {
      const r = await clinicsAdminApi.list();
      setDoctors(r.doctors || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveClinic = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing.clinic,
        duration: Number(editing.clinic.duration) || undefined,
        maxBookingsPerSlot: Number(editing.clinic.maxBookingsPerSlot) || 1,
      };
      if (editing.clinic.id) await clinicsAdminApi.update(editing.clinic.id, payload);
      else await clinicsAdminApi.create({ ...payload, doctorId: editing.doctor.id });
      success('تم حفظ العيادة');
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveDoctor = async () => {
    setBusy(true);
    try {
      await clinicsAdminApi.updateDoctor(doctorEdit.id, {
        name: doctorEdit.name,
        title: doctorEdit.title,
        priceExam: doctorEdit.priceExam,
        priceFollowup: doctorEdit.priceFollowup,
      });
      success('تم حفظ بيانات الطبيب');
      setDoctorEdit(null);
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDay = async (reason) => {
    try {
      const r = await clinicsAdminApi.closeDay({ doctorId: closing.doctor.id, date: closing.date, reason });
      const n = (r.cancelled || []).length;
      success(n ? `أُغلق اليوم وأُلغي ${n} حجز — تواصل مع المرضى من تبويب الإخطارات` : 'أُغلق اليوم');
      setClosing(null);
      onChanged?.();
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!doctors) return <Spinner label="جارٍ تحميل العيادات" variant="inline" />;
  if (doctors.length === 0) {
    return <EmptyState icon="stethoscope" title="لا يوجد أطباء في المنشأة" message="يضيف مدير المستشفى الأطباء من شاشة فريق المستشفى." />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {doctors.map((d) => (
        <div key={d.id} className="glass-panel rounded-2xl p-4">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <span className="tile3d__face tone-cyan !h-11 !w-11 shrink-0" aria-hidden="true">
              <Icon name="stethoscope" size={20} className="tile3d__icon" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-white">{d.name}</p>
              <p className="text-xs text-slate-400">
                {d.profile?.title || 'طبيب'}
                {d.profile?.priceExam ? ` · كشف ${formatMoney(d.profile.priceExam)}` : ''}
              </p>
            </div>
            {mayManage && (
              <Button
                variant="subtle"
                icon="pencil"
                onClick={() =>
                  setDoctorEdit({
                    id: d.id,
                    name: d.name || '',
                    title: d.profile?.title || '',
                    priceExam: d.profile?.priceExam ?? '',
                    priceFollowup: d.profile?.priceFollowup ?? '',
                  })
                }
              >
                الطبيب
              </Button>
            )}
            {mayClose && (
              <Button variant="subtle" icon="calendar-x" onClick={() => setClosing({ doctor: d, date: new Date().toISOString().slice(0, 10) })}>
                إغلاق يوم
              </Button>
            )}
          </div>

          {(d.clinics || []).length === 0 ? (
            <p className="text-sm text-slate-500">لا توجد عيادات لهذا الطبيب.</p>
          ) : (
            <ul className="space-y-2">
              {d.clinics.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-white/[0.03] p-3 text-sm">
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c.color || '#22d3ee' }} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-bold text-white">{c.name}</span>
                    <span className="block text-xs text-slate-400">
                      {c.startTime && c.endTime ? timeRange(c.startTime, c.endTime) : 'مواعيد محددة'} · {c.duration || '—'} د · سعة{' '}
                      {c.maxBookingsPerSlot || 1}
                      {c.examination_price ? ` · ${formatMoney(c.examination_price)}` : ''}
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      {(c.workingDays || [])
                        .map((day) => DAYS.find(([k]) => k === String(day).toLowerCase())?.[1] || day)
                        .join('، ')}
                    </span>
                  </span>
                  {mayManage && (
                    <Button variant="subtle" icon="settings-2" onClick={() => setEditing({ doctor: d, clinic: { ...c } })}>
                      تعديل
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {mayManage && (
            <Button variant="ghost" icon="plus" className="mt-3" onClick={() => setEditing({ doctor: d, clinic: blankClinic() })}>
              عيادة جديدة
            </Button>
          )}
        </div>
      ))}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.clinic?.id ? `تعديل ${editing.clinic.name}` : `عيادة جديدة — ${editing?.doctor?.name || ''}`}
        icon="building-2"
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>
              إلغاء
            </Button>
            <Button icon="save" loading={busy} disabled={!editing?.clinic?.name?.trim()} onClick={saveClinic}>
              حفظ
            </Button>
          </div>
        }
      >
        {editing && <ClinicForm value={editing.clinic} onChange={(clinic) => setEditing((e) => ({ ...e, clinic }))} />}
      </Modal>

      <Modal
        open={Boolean(doctorEdit)}
        onClose={() => setDoctorEdit(null)}
        title="بيانات الطبيب وأسعاره"
        icon="stethoscope"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDoctorEdit(null)}>
              إلغاء
            </Button>
            <Button icon="save" loading={busy} onClick={saveDoctor}>
              حفظ
            </Button>
          </div>
        }
      >
        {doctorEdit && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="الاسم">
              <Input value={doctorEdit.name} onChange={(e) => setDoctorEdit({ ...doctorEdit, name: e.target.value })} />
            </Field>
            <Field label="التخصص / اللقب">
              <Input value={doctorEdit.title} onChange={(e) => setDoctorEdit({ ...doctorEdit, title: e.target.value })} />
            </Field>
            <Field label="سعر الكشف">
              <Input type="number" min="0" value={doctorEdit.priceExam} onChange={(e) => setDoctorEdit({ ...doctorEdit, priceExam: e.target.value })} />
            </Field>
            <Field label="سعر الاستشارة">
              <Input type="number" min="0" value={doctorEdit.priceFollowup} onChange={(e) => setDoctorEdit({ ...doctorEdit, priceFollowup: e.target.value })} />
            </Field>
          </div>
        )}
      </Modal>

      <NoteDialog
        open={Boolean(closing)}
        onClose={() => setClosing(null)}
        onSubmit={closeDay}
        title={`إغلاق يوم — ${closing?.doctor?.name || ''}`}
        label="السبب"
        required={false}
        destructive
        confirmLabel="إغلاق اليوم وإلغاء حجوزاته"
      >
        {closing && (
          <Field label="اليوم" className="mb-3">
            <DateInput value={closing.date} onChange={(e) => setClosing({ ...closing, date: e.target.value })} />
          </Field>
        )}
        <p className="mb-3 rounded-xl bg-red-500/10 p-3 text-xs text-red-200">كل حجوزات هذا الطبيب في ذلك اليوم ستُلغى.</p>
      </NoteDialog>
    </div>
  );
}

export function CheckupRequests() {
  const { can } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [resulting, setResulting] = useState(null);
  const [noting, setNoting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const mayWork = can(PERMISSIONS.CLINIC_MANAGE);

  const load = useCallback(async () => {
    try {
      setRows(await requestsApi.list({ queue: 'clinics', open: '1' }));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (r, action, note) => {
    setBusyId(r.id);
    try {
      await requestsApi.action(r.id, { action, note });
      success(action === 'accept' ? 'تم قبول طلب الكشف' : action === 'reject' ? 'تم الرفض' : 'تم');
      setNoting(null);
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!rows) return <Spinner label="جارٍ تحميل طلبات الكشف" variant="inline" />;
  if (rows.length === 0) return <EmptyState icon="stethoscope" title="لا توجد طلبات كشف مفتوحة" message="يصل هنا ما يوجهه الاستقبال أو الأطباء للكشف." />;

  const cleared = (r) => r.financeStatus === 'approved' || r.priority === 'emergency';

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {rows.map((r) => (
        <RequestCard key={r.id} request={r}>
          {mayWork && ['pending', 'modification_requested'].includes(r.status) && (
            <Button variant="success" icon="check" disabled={!cleared(r)} loading={busyId === r.id} onClick={() => act(r, 'accept')}>
              قبول
            </Button>
          )}
          {mayWork && ['accepted', 'in_progress'].includes(r.status) && (
            <Button variant="success" icon="calendar-check" onClick={() => setResulting(r)}>
              تحديد الموعد وإنهاء الطلب
            </Button>
          )}
          {mayWork && ['pending', 'accepted', 'modification_requested'].includes(r.status) && (
            <Button variant="danger" icon="x-circle" onClick={() => setNoting(r)}>
              رفض
            </Button>
          )}
          {!cleared(r) && ['pending', 'modification_requested'].includes(r.status) && (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-200" icon="hourglass">
              بانتظار إيصال الاستقبال أو موافقة التعاقدات
            </Badge>
          )}
        </RequestCard>
      ))}
      {resulting && <ResultForm key={resulting.id} request={resulting} open onClose={() => setResulting(null)} onDone={load} />}
      <NoteDialog
        open={Boolean(noting)}
        onClose={() => setNoting(null)}
        onSubmit={(note) => act(noting, 'reject', note)}
        title="رفض طلب الكشف"
        label="سبب الرفض"
        destructive
        confirmLabel="رفض"
      />
    </div>
  );
}

const StatusLabel = { pending: 'بانتظار', confirmed: 'مؤكد', in_progress: 'داخل الكشف', completed: 'انتهى', cancelled: 'ملغي' };

/** The day's schedule as a white sheet — one table per clinic. */
export const DaySchedulePrint = forwardRef(function DaySchedulePrint({ columns, date, institutionName }, ref) {
  return (
    <div ref={ref} className="print-sheet" dir="rtl">
      <h1 className="text-lg font-bold">
        {institutionName || 'المنشأة'} — جدول يوم {date}
      </h1>
      {columns.map(({ clinic, doctor, bookings }) => (
        <div key={clinic.id} style={{ marginTop: 14, breakInside: 'avoid' }}>
          <h3 className="font-bold">
            {clinic.name} — {doctor?.name || ''} {clinic.startTime && clinic.endTime ? `(${timeRange(clinic.startTime, clinic.endTime)})` : ''}
          </h3>
          {bookings.length === 0 ? (
            <p className="muted">لا حجوزات</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: '12%' }}>الموعد</th>
                  <th>المريض</th>
                  <th style={{ width: '18%' }}>الهاتف</th>
                  <th style={{ width: '16%' }}>الحالة</th>
                  <th style={{ width: '16%' }}>الحضور</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id}>
                    <td>{b.time}</td>
                    <td>{b.patientName}</td>
                    <td dir="ltr">{b.patientPhone || '—'}</td>
                    <td>{StatusLabel[b.status] || b.status}</td>
                    <td>{b.arrivalFrom ? timeRange(b.arrivalFrom, b.arrivalTo) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
});

/** The button and the preview it opens. */
export function PrintDayButton({ columns, date, institutionName }) {
  const [open, setOpen] = useState(false);
  const sheet = useRef(null);
  return (
    <>
      <Button variant="ghost" icon="printer" onClick={() => setOpen(true)}>
        طباعة الجدول
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="طباعة جدول اليوم"
        icon="printer"
        size="xl"
        footer={
          <div className="flex justify-end">
            <Button icon="printer" onClick={() => printArea(sheet.current)}>
              طباعة
            </Button>
          </div>
        }
      >
        <DaySchedulePrint ref={sheet} columns={columns} date={date} institutionName={institutionName} />
      </Modal>
    </>
  );
}
